import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Footer, Header } from './HomePageLab';
// ═══════════════════════════════════════════════════════════════════════════════
// TABLEAU SYSTÉMIQUE ELEMENTAIRE
// ═══════════════════════════════════════════════════════════════════════════════

const C = {
  bg: '#06060f',
  bgPanel: '#0a0a18',
  bgCard: '#0d0d1e',
  bgInset: '#080814',
  border: 'rgba(167, 139, 250, 0.14)',
  borderSoft: 'rgba(167, 139, 250, 0.07)',
  violet: '#a78bfa',
  white: '#f5f7ff',
  textPrimary: '#e2ecff',
  textSecond: '#c4cbe0',
  textMuted: '#8e94ad',
};
const MONO = '"JetBrains Mono", "Fira Code", monospace';
const DISPLAY = '"Space Grotesk", "Syne", sans-serif';
const BODY = '"Inter", system-ui, sans-serif';

// ─────────────────────────────────────────────────────────────────────────────
// DONNÉES
// ─────────────────────────────────────────────────────────────────────────────

export const FAMILIES = [
  { id: 'contextes', label: 'Les Contextes', short: 'CONTEXTES', color: '#94a3b8', icon: 'layers', description: 'Environnements dans lesquels la décision se prend' },
  { id: 'postures', label: 'Les Postures', short: 'POSTURES', color: '#a78bfa', icon: 'compass', description: "Manières d'être face à la complexité" },
  { id: 'signaux', label: 'Les Signaux Faibles', short: 'SIGNAUX FAIBLES', color: '#fbbf24', icon: 'signal', description: 'Indices annonciateurs de rupture' },
  { id: 'bifurcations', label: 'Les Bifurcations', short: 'BIFURCATIONS', color: '#22d3ee', icon: 'fork', description: 'Moments où une décision oriente la suite' },
  { id: 'dettes', label: 'Les Dettes', short: 'DETTES', color: '#f43f5e', icon: 'scale', description: 'Coûts cachés de la complexité non documentée' },
  { id: 'preuves', label: 'Les Preuves', short: 'PREUVES', color: '#34d399', icon: 'shield', description: 'Traces permettant de documenter les trajectoires' },
  { id: 'accompagnant', label: "Les Postures de l'Accompagnant", short: 'ACCOMPAGNANT', color: '#60a5fa', icon: 'users', description: "Manières d'être de celui qui forme ou recrute" },
];

export const ELEMENTS = [
  { id: 'c_incert', family: 'contextes', label: "Contexte d'incertitude", description: 'Information incomplète, conséquences lourdes' },
  { id: 'c_crise', family: 'contextes', label: 'Contexte de crise', description: 'Urgence, pression, décisions rapides' },
  { id: 'c_routine', family: 'contextes', label: 'Contexte de routine', description: 'Procédures, habitudes, décisions répétitives' },
  { id: 'c_transfo', family: 'contextes', label: 'Contexte de transformation', description: 'Changement, réorganisation, bifurcations' },
  { id: 'ctrl', family: 'postures', code: 'CTRL', label: 'CTRL · Contrôleur', title: 'Le Contrôleur (CTRL)', atlasId: 'controleur', description: 'Cherche à réduire le manque par plus de données' },
  { id: 'orcl', family: 'postures', code: 'ORCL', label: 'ORCL · Oracle', title: "L'Oracle (ORCL)", atlasId: 'oracle', description: 'Comble le manque par interprétation' },
  { id: 'arch', family: 'postures', code: 'ARCH', label: 'ARCH · Architecte', title: "L'Architecte (ARCH)", atlasId: 'architecte', description: "Décide avec ce qu'il a, prévoit des ajustements" },
  { id: 'sage', family: 'postures', code: 'SAGE', label: 'SAGE · Sage', title: 'Le Sage (SAGE)', atlasId: 'sage', description: 'Attend la maturation des signaux' },
  { id: 'expl', family: 'postures', code: 'EXPL', label: 'EXPL · Explorateur', title: "L'Explorateur (EXPL)", atlasId: 'explorateur', description: "Décide en pariant sur l'inconnu" },
  { id: 'oper', family: 'postures', code: 'OPER', label: 'OPER · Opérateur', title: "L'Opérateur (OPER)", atlasId: 'operateur', description: 'Complète avec la procédure standard' },
  { id: 's_ign', family: 'signaux', label: 'Signal ignoré', description: 'Indice non pris en compte' },
  { id: 's_doc', family: 'signaux', label: 'Signal documenté', description: 'Indice noté mais non traité' },
  { id: 's_trait', family: 'signaux', label: 'Signal traité', description: 'Indice ayant conduit à une décision' },
  { id: 'b_open', family: 'bifurcations', label: 'Bifurcation ouverte', description: "Possibilité qui s'ouvre" },
  { id: 'b_closed', family: 'bifurcations', label: 'Bifurcation fermée', description: 'Possibilité qui se ferme' },
  { id: 'b_unused', family: 'bifurcations', label: 'Bifurcation non exploitée', description: 'Possibilité non saisie' },
  { id: 'b_doc', family: 'bifurcations', label: 'Bifurcation documentée', description: 'Bifurcation qui laisse une trace' },
  { id: 'd_cog', family: 'dettes', label: 'Dette cognitive', description: 'Réunions sans décision, surcharge mentale' },
  { id: 'd_emo', family: 'dettes', label: 'Dette émotionnelle', description: 'Épuisement, désengagement, perte de sens' },
  { id: 'd_soc', family: 'dettes', label: 'Dette sociale', description: 'Silos, confiance érodée, ruptures' },
  { id: 'd_dec', family: 'dettes', label: 'Dette décisionnelle', description: 'Décisions bloquées, responsabilités floues' },
  { id: 'd_fin', family: 'dettes', label: 'Dette financière', description: 'Coût systémique annuel' },
  { id: 'p_dec', family: 'preuves', label: 'Trace de décision', description: 'Décision qui laisse une trace' },
  { id: 'p_bif', family: 'preuves', label: 'Trace de bifurcation', description: 'Bifurcation documentée' },
  { id: 'p_frein', family: 'preuves', label: 'Trace de frein', description: 'Frein identifié, levé ou maintenu' },
  { id: 'p_trans', family: 'preuves', label: 'Trace de transformation', description: 'Transformation documentée' },
  { id: 'a_filtre', family: 'accompagnant', label: 'Accompagnant qui filtre', description: 'Sélectionne, juge, exclut' },
  { id: 'a_revele', family: 'accompagnant', label: 'Accompagnant qui révèle', description: 'Documente, observe, révèle' },
  { id: 'a_transmet', family: 'accompagnant', label: 'Accompagnant qui transmet', description: 'Transmet, guide, laisse faire' },
  { id: 'a_controle', family: 'accompagnant', label: 'Accompagnant qui contrôle', description: 'Contrôle, vérifie, impose' },
];

export const LINKS = [
  { from: 'c_incert', to: 'ctrl', rel: 'sollicite', why: "Quand l'information est incomplète, le Contrôleur cherche à combler le manque par toujours plus de données." },
  { from: 'c_incert', to: 'orcl', rel: 'sollicite', why: "Le manque est comblé par l'interprétation plutôt que par la donnée." },
  { from: 'c_incert', to: 'arch', rel: 'sollicite', why: "L'incertitude est le milieu de l'Architecte : il décide avec ce qu'il a et prévoit des ajustements." },
  { from: 'c_incert', to: 'sage', rel: 'sollicite', why: "L'incertitude demande du temps : le Sage attend la maturation des signaux." },
  { from: 'c_crise', to: 'expl', rel: 'pousse', why: "Sous pression, la décision se prend en pariant sur l'inconnu." },
  { from: 'c_crise', to: 'ctrl', rel: 'met en échec', why: "L'urgence rend la quête de données plus coûteuse que la décision elle-même : le délai est dépassé." },
  { from: 'c_routine', to: 'oper', rel: 'installe', why: 'Procédures et habitudes favorisent la complétion par la procédure standard.' },
  { from: 'c_transfo', to: 'expl', rel: 'appelle', why: "Le changement ouvre des champs de possibles que l'Explorateur sait parier." },
  { from: 'c_transfo', to: 'arch', rel: 'appelle', why: "La transformation demande de décider avec ce qu'on a, puis d'ajuster." },
  { from: 'c_crise', to: 's_ign', rel: 'favorise', why: "Dans l'urgence, les signaux faibles passent sous le seuil d'attention." },
  { from: 'c_routine', to: 's_ign', rel: 'favorise', why: "Ce qui sort de la procédure n'est pas vu : l'habitude filtre les signaux." },
  { from: 'c_transfo', to: 'b_open', rel: 'produit', why: 'Une réorganisation fait émerger des possibilités nouvelles.' },
  { from: 'orcl', to: 's_ign', rel: 'recouvre', why: "L'interprétation comble le manque et recouvre le signal au lieu de l'écouter." },
  { from: 'oper', to: 's_ign', rel: 'laisse passer', why: "Un signal hors procédure n'a pas de case où être traité." },
  { from: 'sage', to: 's_doc', rel: 'retient', why: 'Attendre la maturation, c\'est noter les signaux sans (encore) les traiter.' },
  { from: 'arch', to: 's_trait', rel: 'traite', why: 'Décider et prévoir des ajustements, c\'est faire des signaux des décisions.' },
  { from: 's_ign', to: 'b_unused', rel: 'laisse échapper', why: 'Un indice non pris en compte laisse une possibilité non saisie.' },
  { from: 's_doc', to: 'b_unused', rel: 'laisse en suspens', why: "Noté mais non traité : la possibilité est connue, mais elle n'est pas exploitée." },
  { from: 's_trait', to: 'b_open', rel: 'ouvre', why: "Un signal traité conduit à une décision, donc à une possibilité qui s'ouvre." },
  { from: 's_trait', to: 'p_dec', rel: 'appelle une trace', why: 'Un indice ayant conduit à une décision doit laisser la trace de cette décision.' },
  { from: 'b_unused', to: 'd_dec', rel: 'nourrit', why: 'Une possibilité non saisie entretient des décisions bloquées et des responsabilités floues.' },
  { from: 'b_closed', to: 'd_emo', rel: 'nourrit', why: 'Une possibilité qui se ferme sans être comprise entame le sens et l\'engagement.' },
  { from: 'b_doc', to: 'p_bif', rel: 'se conserve en', why: 'Une bifurcation documentée est précisément ce que la trace de bifurcation conserve.' },
  { from: 'p_frein', to: 'b_closed', rel: 'éclaire', why: "La trace de frein dit pourquoi, et par qui, une possibilité s'est fermée." },
  { from: 'ctrl', to: 'd_cog', rel: 'génère', why: 'Chercher plus de données sans trancher produit des réunions sans décision et de la surcharge mentale.' },
  { from: 'ctrl', to: 'd_emo', rel: 'génère', why: 'Tout tenir épuise : épuisement et désengagement sont les coûts du contrôle.' },
  { from: 'orcl', to: 'd_dec', rel: 'génère', why: "L'interprétation sans arbitrage laisse des décisions en suspens." },
  { from: 'd_cog', to: 'd_fin', rel: 'se chiffre dans', why: 'Le temps de réunion sans décision et la surcharge ont un coût annuel.' },
  { from: 'd_emo', to: 'd_fin', rel: 'se chiffre dans', why: "Épuisement, absentéisme et désengagement se traduisent en euros." },
  { from: 'd_soc', to: 'd_fin', rel: 'se chiffre dans', why: 'Silos et ruptures de confiance ont un coût de coordination.' },
  { from: 'd_dec', to: 'd_fin', rel: 'se chiffre dans', why: 'Une décision bloquée retarde et renchérit tout ce qui en dépend.' },
  { from: 'p_dec', to: 'd_dec', rel: 'réduit', why: "Une décision tracée a un responsable et une date : la dette décisionnelle ne s'accumule pas." },
  { from: 'p_trans', to: 'c_transfo', rel: 'documente', why: "La trace de transformation conserve ce qui s'est joué pendant le changement." },
  { from: 'a_filtre', to: 'b_closed', rel: 'ferme', why: "Sélectionner, juger, exclure ferme des possibilités pour l'autre." },
  { from: 'a_filtre', to: 'd_soc', rel: 'alimente', why: 'Le tri et l\'exclusion produisent des silos et érodent la confiance.' },
  { from: 'a_controle', to: 'ctrl', rel: 'reproduit', why: 'Un accompagnant qui contrôle forme des contrôleurs.' },
  { from: 'a_controle', to: 'oper', rel: 'forme', why: 'Contrôler et imposer produit des opérateurs qui appliquent la procédure.' },
  { from: 'a_controle', to: 'd_emo', rel: 'alimente', why: 'Contrôler, vérifier, imposer épuise et désengage.' },
  { from: 'a_revele', to: 's_doc', rel: 'produit', why: 'Observer et documenter fait exister les signaux faibles.' },
  { from: 'a_revele', to: 'b_doc', rel: 'produit', why: 'Révéler, c\'est faire laisser une trace aux bifurcations.' },
  { from: 'a_revele', to: 'p_dec', rel: 'produit', why: 'Documenter les décisions pour que le parcours devienne lisible.' },
  { from: 'a_revele', to: 'sage', rel: 'forme', why: "Révéler plutôt qu'enseigner : l'autre apprend à laisser mûrir." },
  { from: 'a_transmet', to: 'arch', rel: 'forme', why: "Transmettre et laisser faire prépare à décider avec ce qu'on a." },
];

export const UNLOCKABLES = [
  {
    id: 'centre_ajustement',
    name: "Le Centre d'Ajustement",
    description: "Pour simuler et tester vos décisions.",
    requiredCombinations: [
      ['c_incert', 'b_open', 'arch'],
      ['c_crise', 'b_open', 'expl'],
      ['c_transfo', 'b_open', 'sage']
    ],
    unlockMessage: "Vous avez identifié une bifurcation et une posture pour y faire face. Le Centre d'Ajustement vous permet de tester ce scénario.",
    url: '/centre-ajustement'
  },
  {
    id: 'parcours_engine',
    name: "Parcours Engine",
    description: "Pour documenter et structurer les trajectoires.",
    requiredCombinations: [
      ['a_revele', 's_trait', 'p_dec'],
      ['a_transmet', 's_doc', 'p_bif'],
      ['a_revele', 'b_doc', 'p_trans']
    ],
    unlockMessage: "Vous avez accompagné un signal jusqu'à sa trace. Parcours Engine vous permet de transformer cette trace en parcours documenté.",
    url: '/parcours'
  }
];

const DEFAULT_DATA = { families: FAMILIES, elements: ELEMENTS, links: LINKS };

// ─────────────────────────────────────────────────────────────────────────────
// ICÔNES
// ─────────────────────────────────────────────────────────────────────────────

const ICONS = {
  compass: (<><circle cx="12" cy="12" r="9" /><polygon points="15.5 8.5 13.5 13.5 8.5 15.5 10.5 10.5" /></>),
  scale: (<><path d="M12 4v16" /><path d="M7 20h10" /><path d="M5 7h14" /><path d="M5 7l-3 7a3.5 3.5 0 0 0 6 0z" /><path d="M19 7l-3 7a3.5 3.5 0 0 0 6 0z" /></>),
  fork: (<><path d="M12 21v-9" /><path d="M12 12L6 5" /><path d="M12 12l6-7" /><path d="M6 9V5h4" /><path d="M14 5h4v4" /></>),
  signal: (<><circle cx="12" cy="12" r="1.6" /><path d="M8.6 8.6a5 5 0 0 0 0 6.8" /><path d="M15.4 8.6a5 5 0 0 1 0 6.8" /><path d="M5.6 5.6a9 9 0 0 0 0 12.8" /><path d="M18.4 5.6a9 9 0 0 1 0 12.8" /></>),
  shield: (<><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
  users: (<><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14a5 5 0 0 1 5 6" /></>),
  layers: (<><polygon points="12 3 21 8 12 13 3 8" /><path d="M3 12l9 5 9-5" /><path d="M3 16l9 5 9-5" /></>),
  chevron: (<><polyline points="6 9 12 15 18 9" /></>),
};

const Icon = ({ name, size = 16, color = 'currentColor', strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// GÉOMÉTRIE
// ─────────────────────────────────────────────────────────────────────────────

const VB = 1010;
const CX = VB / 2;
const CY = VB / 2;
const R_HUB = 58;
const R_NODE = 225;
const LABEL_GAP = 20;
const R_BAND_IN = 462;
const R_BAND_OUT = 498;

const polar = (r, deg) => {
  const a = (deg * Math.PI) / 180;
  return { x: CX + r * Math.sin(a), y: CY - r * Math.cos(a) };
};
const f = (n) => Math.round(n * 100) / 100;
const pt = (p) => `${f(p.x)} ${f(p.y)}`;

const annularSector = (r0, r1, a0, a1) => {
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${pt(polar(r1, a0))} A ${r1} ${r1} 0 ${large} 1 ${pt(polar(r1, a1))} L ${pt(polar(r0, a1))} A ${r0} ${r0} 0 ${large} 0 ${pt(polar(r0, a0))} Z`;
};

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    // Support anciens navigateurs
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [breakpoint]);

  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export const TableauSystemique = ({ data = DEFAULT_DATA, className, style }) => {
  const { families, elements, links } = data;
  const uid = useRef(Math.random().toString(36).slice(2, 8)).current;
  const wrapRef = useRef(null);

  const [selected, setSelected] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [tip, setTip] = useState(null);
  const isMobile = useIsMobile();

  const [unlockedTools, setUnlockedTools] = useState([]);
  const [isToolMenuOpen, setIsToolMenuOpen] = useState(false);
  const [trajectory, setTrajectory] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);

  const familyMap = useMemo(() => new Map(families.map(fm => [fm.id, fm])), [families]);
  const elementMap = useMemo(() => new Map(elements.map(e => [e.id, e])), [elements]);

  const layout = useMemo(() => {
    const span = 360 / families.length;
    const pad = 3.2;
    const sectors = families.map((fm, i) => {
      const center = i * span;
      return { family: fm, center, a0: center - span / 2 + 0.6, a1: center + span / 2 - 0.6 };
    });
    const nodes = new Map();
    sectors.forEach(s => {
      const members = elements.filter(e => e.family === s.family.id);
      members.forEach((el, k) => {
        const angle = s.center - span / 2 + pad + (span - 2 * pad) * ((k + 0.5) / members.length);
        const p = polar(R_NODE, angle);
        nodes.set(el.id, { el, angle, x: p.x, y: p.y, family: s.family });
      });
    });
    return { sectors, nodes, span };
  }, [families, elements]);

  const { resolvedLinks, adjacency } = useMemo(() => {
    const adj = new Map(elements.map(e => [e.id, { out: [], in: [] }]));
    const resolved = [];
    links.forEach((l, i) => {
      const a = layout.nodes.get(l.from);
      const b = layout.nodes.get(l.to);
      if (!a || !b) return;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const ctrl = { x: CX + (mid.x - CX) * 0.18, y: CY + (mid.y - CY) * 0.18 };
      const link = { ...l, index: i, a, b, d: `M ${pt(a)} Q ${pt(ctrl)} ${pt(b)}` };
      resolved.push(link);
      adj.get(l.from).out.push(link);
      adj.get(l.to).in.push(link);
    });
    return { resolvedLinks: resolved, adjacency: adj };
  }, [links, layout, elements]);

  const focusElementId = selected?.type === 'element' ? selected.id : (selected ? null : hoverId);
  const focusFamilyId = selected?.type === 'family' ? selected.id : null;

  const focus = useMemo(() => {
    if (focusElementId) {
      const adj = adjacency.get(focusElementId);
      const nodeSet = new Set([focusElementId]);
      const linkSet = new Set();
      [...adj.out, ...adj.in].forEach(l => { nodeSet.add(l.from); nodeSet.add(l.to); linkSet.add(l.index); });
      return { mode: 'element', nodes: nodeSet, links: linkSet, strong: new Set([focusElementId]) };
    }
    if (focusFamilyId) {
      const own = new Set(elements.filter(e => e.family === focusFamilyId).map(e => e.id));
      const nodeSet = new Set(own);
      const linkSet = new Set();
      resolvedLinks.forEach(l => {
        if (own.has(l.from) || own.has(l.to)) { linkSet.add(l.index); nodeSet.add(l.from); nodeSet.add(l.to); }
      });
      return { mode: 'family', nodes: nodeSet, links: linkSet, strong: own };
    }
    return { mode: 'none', nodes: null, links: null, strong: null };
  }, [focusElementId, focusFamilyId, adjacency, resolvedLinks, elements]);

  

  const clear = useCallback(() => { setSelected(null); setTip(null); }, []);
  
  const selectElement = useCallback((id) => {
    const isCurrentlySelected = selected?.type === 'element' && selected.id === id;
    setSelected(isCurrentlySelected ? null : { type: 'element', id });
    
    if (!isCurrentlySelected) {
      setTrajectory(prevTraj => {
        if (prevTraj[prevTraj.length - 1] === id) return prevTraj;
        
        const newTraj = [...prevTraj, id];
        
        UNLOCKABLES.forEach(tool => {
          const isUnlocked = tool.requiredCombinations.some(combination => 
            combination.every(req => newTraj.includes(req))
          );
          
          // On vérifie si l'outil n'est pas déjà dans le tableau
          if (isUnlocked && !unlockedTools.some(t => t.id === tool.id)) {
            setUnlockedTools(prev => [...prev, tool]);
          }
        });
        
        return newTraj;
      });
    }
  }, [selected, unlockedTools]);

  const selectFamily = useCallback((id) => {
    setSelected(prev => (prev?.type === 'family' && prev.id === id ? null : { type: 'family', id }));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { clear(); setIsToolMenuOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clear]);

  const showTip = useCallback((id, target) => {
    const wrap = wrapRef.current;
    if (!wrap || !target) return;
    const wr = wrap.getBoundingClientRect();
    const tr = target.getBoundingClientRect();
    setTip({ id, x: tr.left - wr.left + tr.width / 2, y: tr.top - wr.top, w: wr.width });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  const nodeOpacity = (id) => {
    if (focus.mode === 'none') return 1;
    if (focus.strong.has(id)) return 1;
    if (focus.nodes.has(id)) return focus.mode === 'element' ? 1 : 0.7;
    return 0.22;
  };
  const linkStyle = (l) => {
    if (focus.mode === 'none') return { opacity: 0.2, width: 1.3, active: false };
    if (focus.links.has(l.index)) return { opacity: focus.mode === 'element' ? 0.95 : 0.55, width: focus.mode === 'element' ? 2.4 : 1.6, active: true };
    return { opacity: 0.035, width: 1, active: false };
  };

  const tipEl = tip ? elementMap.get(tip.id) : null;
  const tipFamily = tipEl ? familyMap.get(tipEl.family) : null;
  const linkCount = resolvedLinks.length;

  return (
    <div ref={wrapRef} className={`tse-root ${className || ''}`}
        style={{
          position: 'relative', background: C.bg, color: C.textPrimary, fontFamily: BODY,
          padding: isMobile ? '16px 16px 24px' : '28px 24px 24px',
          minHeight: isMobile ? '100vh' : undefined,
          display: isMobile ? 'flex' : undefined,
          flexDirection: isMobile ? 'column' : undefined,
          ...style
        }}>
      <style>{CSS}</style>

      <Header />

      {/* ── Modale d'accueil — uniquement desktop ── */}
      {!isMobile && showWelcome && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(6, 6, 15, 0.85)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: 24, overflowY: 'auto'
        }}>
          <div style={{
            background: C.bgPanel, border: `1px solid ${C.violet}`, borderRadius: 12,
            maxWidth: 680, width: '100%', padding: 32,
            boxShadow: `0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px ${C.violet}33`,
            maxHeight: '100%', overflowY: 'auto', margin: 'auto'
          }}>
            <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.2em', color: C.violet, marginBottom: 12 }}>
              PESACORE · ARCHITECTURE
            </div>
            <h2 style={{ fontFamily: DISPLAY, fontSize: 28, fontWeight: 700, color: C.white, marginBottom: 16 }}>
              Bienvenue dans le Tableau Systémique Élémentaire.
            </h2>

            <div style={{ fontSize: 16, lineHeight: 1.7, color: C.textSecond, marginBottom: 24 }}>
              <p style={{ marginBottom: 16 }}>
                « J'ai conçu ce Tableau comme une grammaire de la décision. Tout comme le tableau périodique de Mendeleïev a structuré notre compréhension de la matière, ce Tableau vise à structurer notre lecture de la complexité. Nous y avons classé les éléments qui traversent toute organisation : les contextes, les postures, les signaux, les bifurcations, les dettes et les preuves.
              </p>
              <p>
                Considérez ce Tableau comme un espace de lecture : il vous permet de situer une problématique, de comprendre les liens de cause à effet, et de révéler ce qui se joue vraiment quand il faut trancher. »
              </p>
              <p style={{ fontFamily: MONO, fontSize: 13, color: C.violet, marginTop: 12, fontStyle: 'italic' }}>
                — Farid Aït-Mahrez, fondateur de Pesacore Architect
              </p>
            </div>

            <div style={{ background: C.bgInset, padding: 16, borderRadius: 8, marginBottom: 24, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted, marginBottom: 8, letterSpacing: '0.1em' }}>COMMENT L'UTILISER</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 15, lineHeight: 1.6, color: C.textSecond }}>
                <li style={{ marginBottom: 6 }}><strong>Explorez :</strong> Survolez les éléments pour comprendre ce qu'ils sont. Cliquez sur l'un d'eux pour voir ses connexions.</li>
                <li style={{ marginBottom: 6 }}><strong>Tissez :</strong> En sélectionnant des éléments, vous construisez une trajectoire de pensée.</li>
                <li><strong>Débloquez :</strong> En combinant les bons éléments, vous débloquerez l'accès aux outils pertinents de la plateforme.</li>
              </ul>
            </div>

            <button
              onClick={() => setShowWelcome(false)}
              style={{
                width: '100%', padding: '14px', background: C.violet, color: C.bg,
                border: 'none', borderRadius: 8, fontFamily: MONO, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', letterSpacing: '0.05em', transition: 'transform 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Commencer l'exploration
            </button>
          </div>
        </div>
      )}

      {/* ── Message mobile : expérience desktop requise ── */}
      {isMobile && (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 8px',
        }}>
          <div style={{
            background: C.bgPanel,
            border: `1px solid ${C.violet}44`,
            borderRadius: 16,
            padding: '32px 24px',
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
            boxShadow: `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px ${C.violet}22`,
          }}>
            {/* Icône desktop */}
            <div style={{
              width: 72, height: 72, margin: '0 auto 24px',
              borderRadius: '50%',
              background: `${C.violet}15`,
              border: `1px solid ${C.violet}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                  stroke={C.violet} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>

            <div style={{
              fontFamily: MONO, fontSize: 11, letterSpacing: '0.2em',
              color: C.violet, marginBottom: 12
            }}>
              EXPÉRIENCE DESKTOP
            </div>

            <h2 style={{
              fontFamily: DISPLAY, fontSize: 24, fontWeight: 700,
              color: C.white, margin: '0 0 16px', lineHeight: 1.2
            }}>
              Le Tableau se lit sur grand écran.
            </h2>

            <p style={{
              fontSize: 15, lineHeight: 1.65,
              color: C.textSecond, margin: '0 0 16px'
            }}>
              Le Tableau Systémique Élémentaire a été conçu comme une
              <strong style={{ color: C.white }}> carte interactive</strong> :
              les familles se lisent en périphérie, les liens se révèlent au survol,
              et chaque élément s'explore par connexion.
            </p>

            <p style={{
              fontSize: 15, lineHeight: 1.65,
              color: C.textSecond, margin: '0 0 24px'
            }}>
              Pour en préserver toute la valeur, nous vous invitons à
              l'ouvrir depuis un ordinateur.
            </p>

            <div style={{
              padding: '14px 16px',
              background: C.bgInset,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 8,
              fontFamily: MONO,
              fontSize: 12,
              color: C.textMuted,
              letterSpacing: '0.05em',
              lineHeight: 1.5,
            }}>
              <div style={{ color: C.violet, marginBottom: 6, fontWeight: 700 }}>
                VOUS ÊTES SUR MOBILE
              </div>
              Copiez ce lien et ouvrez-le sur votre ordinateur :
              <div style={{
                marginTop: 8, padding: '6px 8px',
                background: C.bg, borderRadius: 4,
                color: C.textSecond, fontSize: 11,
                wordBreak: 'break-all',
                fontFamily: MONO,
              }}>
                {typeof window !== 'undefined' ? window.location.href : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Contenu desktop (tout le reste) ── */}
      {!isMobile && (
        <>
          {/* ── En-tête avec le bouton d'outils débloqués ── */}
          <header style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            flexWrap: 'wrap', gap: 20, marginBottom: 24, marginTop: 34
          }}>
            <div style={{ flex: '1 1 500px', minWidth: 0 }}>
              <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.2em', color: C.violet, marginBottom: 8 }}>
                CADRE DE LECTURE
              </div>
              <h2 style={{
                fontFamily: DISPLAY, fontWeight: 700,
                fontSize: 'clamp(26px, 3.2vw, 38px)',
                lineHeight: 1.12, margin: '0 0 8px', color: C.white
              }}>
                Tableau Systémique Élémentaire
              </h2>
              <p style={{ margin: 0, maxWidth: 760, fontSize: 15, lineHeight: 1.6, color: C.textSecond }}>
                Lire et documenter la complexité dans les organisations : {families.length} familles, {elements.length} éléments
                et {linkCount} liens qui montrent comment l'un peut entraîner l'autre.
              </p>
            </div>

            {unlockedTools.length > 0 && (
              <div style={{ position: 'relative', marginTop: 10 }}>
                <button
                  onClick={() => setIsToolMenuOpen(!isToolMenuOpen)}
                  className="tse-unlock-pulse"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 20px', borderRadius: 8,
                    background: C.violet, color: C.bg,
                    fontWeight: 700, fontFamily: MONO, fontSize: 14, letterSpacing: '0.05em',
                    textDecoration: 'none', border: 'none', cursor: 'pointer',
                    boxShadow: `0 0 20px ${C.violet}aa`
                  }}
                >
                  <Icon name="shield" size={18} color={C.bg} />
                  {unlockedTools.length === 1
                    ? `ACCÉDER À ${unlockedTools[0].name.toUpperCase()}`
                    : `${unlockedTools.length} OUTILS DÉBLOQUÉS`}
                  <Icon name="chevron" size={18} color={C.bg} />
                </button>

                {isToolMenuOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 8,
                    background: C.bgPanel, border: `1px solid ${C.violet}77`, borderRadius: 8,
                    padding: 16, width: 340, maxHeight: '60vh', overflowY: 'auto',
                    zIndex: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.6)'
                  }}>
                    {unlockedTools.map((tool) => (
                      <div key={tool.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.borderSoft}` }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: C.white, marginBottom: 6 }}>{tool.name}</div>
                        <p style={{ fontSize: 13, color: C.textSecond, marginBottom: 10, lineHeight: 1.5 }}>{tool.unlockMessage}</p>
                        <a
                          href={tool.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsToolMenuOpen(false)}
                          style={{
                            display: 'block', textAlign: 'center', padding: '10px',
                            background: C.violet, color: C.bg, border: 'none', borderRadius: 6,
                            cursor: 'pointer', fontWeight: 600, textDecoration: 'none'
                          }}
                        >
                          Ouvrir l'outil
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </header>

          {/* ── Légende / filtre par famille ── */}
          <nav aria-label="Familles" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
            {families.map(fm => {
              const active = selected?.type === 'family' && selected.id === fm.id;
              const count = elements.filter(e => e.family === fm.id).length;
              return (
                <button key={fm.id} className="tse-chip" aria-pressed={active} onClick={() => selectFamily(fm.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                    background: active ? fm.color + '1f' : C.bgInset,
                    border: `1px solid ${active ? fm.color + 'aa' : C.border}`,
                    color: active ? C.white : C.textSecond, fontFamily: BODY, fontSize: 14, fontWeight: 500,
                  }}>
                  <Icon name={fm.icon} size={17} color={fm.color} />
                  {fm.label.replace(/^Les /, '')}
                  <span style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted }}>{count}</span>
                </button>
              );
            })}
          </nav>

          <div className="tse-grid">
            <div style={{ position: 'relative', minWidth: 0 }}>
              <svg viewBox={`0 0 ${VB} ${VB}`} role="group" aria-label="Diagramme radial du tableau systémique élémentaire"
                  style={{ width: '100%', height: 'auto', maxHeight: '86vh', display: 'block', margin: '0 auto' }}
                  onClick={clear}>
                <defs>
                  {resolvedLinks.map(l => (
                    <linearGradient key={l.index} id={`${uid}-g${l.index}`} gradientUnits="userSpaceOnUse"
                                    x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y}>
                      <stop offset="0%" stopColor={l.a.family.color} />
                      <stop offset="100%" stopColor={l.b.family.color} />
                    </linearGradient>
                  ))}
                  <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.10" />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
                  </radialGradient>
                </defs>

                <circle cx={CX} cy={CY} r={R_BAND_IN} fill={`url(#${uid}-glow)`} />

                {[100, 160].map(r => <circle key={r} cx={CX} cy={CY} r={r} fill="none" stroke={C.borderSoft} strokeWidth="1" />)}
                <circle cx={CX} cy={CY} r={R_NODE} fill="none" stroke={C.border} strokeWidth="1" strokeDasharray="2 7" />

                {layout.sectors.map(s => {
                  const p0 = polar(R_HUB + 14, s.center - layout.span / 2);
                  const p1 = polar(R_BAND_IN, s.center - layout.span / 2);
                  return <line key={`sep-${s.family.id}`} x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke={C.borderSoft} strokeWidth="1" />;
                })}

                {layout.sectors.map(s => {
                  const fm = s.family;
                  const isFocus = focusFamilyId === fm.id || (focus.mode === 'element' && elementMap.get(focusElementId)?.family === fm.id);
                  const dim = focus.mode !== 'none' && !isFocus;
                  const bottom = s.center > 90 && s.center < 270;
                  const rt = (R_BAND_IN + R_BAND_OUT) / 2;
                  const tA0 = s.a0 + 1, tA1 = s.a1 - 1;
                  const arcId = `${uid}-arc-${fm.id}`;
                  const arcD = bottom
                    ? `M ${pt(polar(rt, tA1))} A ${rt} ${rt} 0 0 0 ${pt(polar(rt, tA0))}`
                    : `M ${pt(polar(rt, tA0))} A ${rt} ${rt} 0 0 1 ${pt(polar(rt, tA1))}`;
                  const iconPos = polar((R_BAND_IN + R_BAND_OUT) / 2, s.a0 + 5.2);
                  return (
                    <g key={fm.id} className="tse-band" style={{ opacity: dim ? 0.45 : 1, cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); selectFamily(fm.id); }}>
                      <path d={annularSector(R_BAND_IN, R_BAND_OUT, s.a0, s.a1)}
                            fill={fm.color + (isFocus ? '40' : '1c')} stroke={fm.color + (isFocus ? 'ee' : '66')} strokeWidth={isFocus ? 1.6 : 1} />
                      <path id={arcId} d={arcD} fill="none" />
                      <text fontFamily={MONO} fontSize="15" fontWeight="700" letterSpacing="2.2" fill={fm.color}>
                        <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">{fm.short}</textPath>
                      </text>
                      <g transform={`translate(${f(iconPos.x - 10)} ${f(iconPos.y - 10)}) scale(0.84)`}>
                        <Icon name={fm.icon} size={24} color={fm.color} strokeWidth={2} />
                      </g>
                    </g>
                  );
                })}

                <g>
                  {[...resolvedLinks].sort((x, y) => Number(linkStyle(x).active) - Number(linkStyle(y).active)).map(l => {
                    const st = linkStyle(l);
                    return (
                      <path key={l.index} d={l.d} className={`tse-link ${st.active && focus.mode === 'element' ? 'tse-flow' : ''}`}
                            stroke={`url(#${uid}-g${l.index})`} strokeWidth={st.width} strokeLinecap="round"
                            style={{ opacity: st.opacity }} />
                    );
                  })}
                </g>

                <circle cx={CX} cy={CY} r={R_HUB} fill={C.bgCard} stroke={C.border} strokeWidth="1.5" />
                <text x={CX} y={CY - 8} textAnchor="middle" fontFamily={MONO} fontSize="13" fontWeight="700" letterSpacing="3" fill={C.textSecond}>TABLEAU</text>
                <text x={CX} y={CY + 10} textAnchor="middle" fontFamily={MONO} fontSize="13" fontWeight="700" letterSpacing="2" fill={C.textSecond}>SYSTÉMIQUE</text>

                {focus.mode !== 'none' && (
                  <text x={CX} y={CY + 30} textAnchor="middle" fontFamily={MONO} fontSize="12" fill={C.violet}>
                    {focus.links.size} lien{focus.links.size > 1 ? 's' : ''}
                  </text>
                )}

                {[...layout.nodes.values()].map(n => {
                  const { el, angle, family } = n;
                  const outward = angle - 90;
                  const flip = angle > 180;
                  const rot = flip ? outward + 180 : outward;
                  const isSel = selected?.type === 'element' && selected.id === el.id;
                  const strong = focus.mode !== 'none' && focus.strong.has(el.id);
                  const op = nodeOpacity(el.id);
                  return (
                    <g key={el.id} className="tse-node" tabIndex={0} role="button"
                      aria-label={`${el.title || el.label} — ${family.label}. ${el.description}`} aria-pressed={isSel}
                      transform={`translate(${f(n.x)} ${f(n.y)}) rotate(${f(rot)})`} style={{ opacity: op }}
                      onClick={(e) => { e.stopPropagation(); selectElement(el.id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); selectElement(el.id); } }}
                      onMouseEnter={(e) => { setHoverId(el.id); showTip(el.id, e.currentTarget.querySelector('.tse-ring')); }}
                      onMouseLeave={() => { setHoverId(null); hideTip(); }}
                      onFocus={(e) => { setHoverId(el.id); showTip(el.id, e.currentTarget.querySelector('.tse-ring')); }}
                      onBlur={() => { setHoverId(null); hideTip(); }}>
                      {(isSel || strong) && <circle r="22" fill={family.color} opacity="0.18" />}
                      <circle className="tse-ring" r="12" fill={family.color + '30'} stroke={family.color} strokeWidth={isSel ? 3 : 2} />
                      <circle r="4" fill={family.color} />
                      <rect x={flip ? -(LABEL_GAP + 215) : 8} y="-12" width={LABEL_GAP + 215} height="24" fill="transparent" />
                      <text x={flip ? -LABEL_GAP : LABEL_GAP} y="0" dominantBaseline="central" textAnchor={flip ? 'end' : 'start'}
                            fontFamily={BODY} fontSize="16" fontWeight={strong ? 600 : 500}
                            fill={isSel ? family.color : (strong ? C.white : C.textSecond)}>
                        {el.label}
                      </text>
                    </g>
                  );
                })}
              </svg>

              {tipEl && tip && (
                <div role="tooltip" style={{
                  position: 'absolute', left: Math.max(150, Math.min((tip.w || 0) - 150, tip.x)), top: tip.y - 12, transform: 'translate(-50%, -100%)',
                  width: 280, padding: '10px 12px', borderRadius: 6, pointerEvents: 'none', zIndex: 5,
                  background: C.bgPanel + 'f2', border: `1px solid ${tipFamily.color}88`, boxShadow: `0 6px 24px #000a, 0 0 0 1px ${tipFamily.color}22`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                    <Icon name={tipFamily.icon} size={14} color={tipFamily.color} />
                    <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', color: tipFamily.color }}>{tipFamily.short}</span>
                  </div>
                  <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 16, color: C.white, marginBottom: 4 }}>{tipEl.title || tipEl.label}</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: C.textSecond }}>{tipEl.description}</div>
                  <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 11, color: C.textMuted }}>
                    {(adjacency.get(tipEl.id)?.out.length || 0) + (adjacency.get(tipEl.id)?.in.length || 0)} connexions · cliquer pour les voir
                  </div>
                </div>
              )}
            </div>

            <aside aria-live="polite" style={{ background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 0 }}>
              <Panel
                selected={selected} families={families} elements={elements} familyMap={familyMap} elementMap={elementMap}
                adjacency={adjacency} resolvedLinks={resolvedLinks}
                onSelectElement={selectElement}
                onSelectFamily={selectFamily}
                onClear={clear}
              />
            </aside>
          </div>

          <Footer />
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PANNEAU LATÉRAL
// ─────────────────────────────────────────────────────────────────────────────

const Eyebrow = ({ children, color = C.textMuted }) => (
  <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.14em', color, marginBottom: 8 }}>{children}</div>
);

const FamilyBadge = ({ family }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 5,
                background: family.color + '1c', border: `1px solid ${family.color}66` }}>
    <Icon name={family.icon} size={16} color={family.color} />
    <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.1em', color: family.color }}>{family.short}</span>
  </div>
);

const CloseBtn = ({ onClick }) => (
  <button onClick={onClick} aria-label="Fermer (Échap)" title="Fermer (Échap)"
    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 4, width: 30, height: 30, cursor: 'pointer', color: C.textMuted, fontSize: 18, lineHeight: 1 }}>
    ×
  </button>
);

const LinkRow = ({ link, otherId, direction, elementMap, familyMap, onSelect }) => {
  const other = elementMap.get(otherId);
  const fam = familyMap.get(other.family);
  return (
    <button className="tse-row" onClick={() => onSelect(otherId)}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 8, borderRadius: 6, cursor: 'pointer',
               background: C.bgInset, border: `1px solid ${C.border}`, borderLeft: `3px solid ${fam.color}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 12, color: fam.color, letterSpacing: '0.04em' }}>
          {direction === 'out' ? `${link.rel} →` : `← ${link.rel}`}
        </span>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.white }}>{other.title || other.label}</span>
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: C.textSecond }}>{link.why}</div>
    </button>
  );
};

const Panel = ({ selected, families, elements, familyMap, elementMap, adjacency, resolvedLinks, onSelectElement, onSelectFamily, onClear }) => {
  
  if (!selected) {
    return (
      <div style={{ padding: 18 }}>
        <Eyebrow color={C.violet}>COMMENT LIRE LE SYSTÈME</Eyebrow>
        <p style={{ margin: '0 0 14px', fontSize: 15, lineHeight: 1.65, color: C.textSecond }}>
          Chaque secteur est une <strong style={{ color: C.white }}>famille</strong>, chaque point un <strong style={{ color: C.white }}>élément</strong>.
          Les courbes passant par le centre sont les liens : elles montrent ce qu'un élément entraîne ou subit.
        </p>
        <ul style={{ margin: '0 0 18px', padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
          {[['Survoler', 'la description complète'], ['Cliquer', "les connexions et leur justification"], ['Cliquer un secteur', 'isoler une famille'], ['Échap', 'revenir à la vue d\'ensemble']].map(([k, v]) => (
            <li key={k} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 14, color: C.textSecond }}>
              <span style={{ fontFamily: MONO, fontSize: 12, color: C.violet, minWidth: 128 }}>{k}</span>{v}
            </li>
          ))}
        </ul>
        <Eyebrow>LES {families.length} FAMILLES</Eyebrow>
        {families.map(fm => (
          <button key={fm.id} className="tse-row" onClick={() => onSelectFamily(fm.id)}
            style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 8, borderRadius: 6, cursor: 'pointer',
                     background: C.bgInset, border: `1px solid ${C.border}`, borderLeft: `3px solid ${fm.color}` }}>
            <span style={{ marginTop: 2 }}><Icon name={fm.icon} size={18} color={fm.color} /></span>
            <span>
              <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.white }}>{fm.label}</span>
              <span style={{ display: 'block', fontSize: 14, lineHeight: 1.5, color: C.textSecond }}>{fm.description}</span>
            </span>
          </button>
        ))}
        <p style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.55, color: C.textMuted, fontStyle: 'italic' }}>
          Les liens sont une proposition de lecture, il ne s'agit pas d'une vérité figée : ils se discutent, se corrigent et se complètent.
        </p>
      </div>
    );
  }

  if (selected.type === 'family') {
    const fm = familyMap.get(selected.id);
    const members = elements.filter(e => e.family === fm.id);
    const own = new Set(members.map(m => m.id));
    const cross = new Map();
    resolvedLinks.forEach(l => {
      const fa = own.has(l.from), fb = own.has(l.to);
      if (fa === fb) return;
      const otherEl = elementMap.get(fa ? l.to : l.from);
      const rec = cross.get(otherEl.family) || { out: 0, in: 0 };
      if (fa) rec.out += 1; else rec.in += 1;
      cross.set(otherEl.family, rec);
    });
    return (
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <FamilyBadge family={fm} /><CloseBtn onClick={onClear} />
        </div>
        <h3 style={{ margin: '0 0 6px', fontFamily: DISPLAY, fontSize: 22, fontWeight: 700, color: C.white }}>{fm.label}</h3>
        <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.6, color: C.textSecond }}>{fm.description}</p>
        <Eyebrow color={fm.color}>ÉLÉMENTS ({members.length})</Eyebrow>
        {members.map(m => (
          <button key={m.id} className="tse-row" onClick={() => onSelectElement(m.id)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 8, borderRadius: 6, cursor: 'pointer',
                     background: C.bgInset, border: `1px solid ${C.border}`, borderLeft: `3px solid ${fm.color}` }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.white }}>{m.title || m.label}</span>
            <span style={{ display: 'block', fontSize: 14, lineHeight: 1.5, color: C.textSecond }}>{m.description}</span>
          </button>
        ))}
        {cross.size > 0 && (
          <>
            <div style={{ height: 10 }} />
            <Eyebrow>RELIÉE À</Eyebrow>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[...cross.entries()].map(([fid, c]) => {
                const of = familyMap.get(fid);
                return (
                  <button key={fid} className="tse-chip" onClick={() => onSelectFamily(fid)}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 10px', borderRadius: 5, cursor: 'pointer',
                             background: C.bgInset, border: `1px solid ${of.color}66`, color: C.textSecond, fontSize: 14 }}>
                    <Icon name={of.icon} size={15} color={of.color} />
                    {of.label.replace(/^Les /, '')}
                    <span style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted }}>{c.out}→ {c.in}←</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  const el = elementMap.get(selected.id);
  const fm = familyMap.get(el.family);
  const adj = adjacency.get(el.id);
  const total = adj.out.length + adj.in.length;
  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <button onClick={() => onSelectFamily(fm.id)} title="Isoler cette famille" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
          <FamilyBadge family={fm} />
        </button>
        <CloseBtn onClick={onClear} />
      </div>
      <h3 style={{ margin: '0 0 6px', fontFamily: DISPLAY, fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: C.white }}>{el.title || el.label}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 16, lineHeight: 1.6, color: C.textSecond }}>{el.description}</p>

      {el.atlasId && (
        <a 
          href={`/atlas-posture`}
          target="_blank" 
          rel="noopener noreferrer"
          className="tse-atlas-pulse"
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', marginBottom: 16, padding: '10px 12px', 
            borderRadius: 6, cursor: 'pointer', 
            background: C.violet + '1c',
            border: `1px solid ${C.violet}77`, 
            color: C.violet, 
            fontFamily: MONO, fontSize: 13, letterSpacing: '0.08em',
            textDecoration: 'none'
          }}
        >
          <Icon name="compass" size={14} color={C.violet} />
          VOIR CETTE POSTURE DANS L'ATLAS
        </a>
      )}

      <Eyebrow color={fm.color}>CONNEXIONS ({total})</Eyebrow>
      {total === 0 && <p style={{ fontSize: 14, color: C.textMuted }}>Aucune connexion proposée pour cet élément.</p>}

      {adj.out.length > 0 && (
        <>
          <div style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted, margin: '4px 0 8px' }}>AGIT SUR ({adj.out.length})</div>
          {adj.out.map(l => <LinkRow key={l.index} link={l} otherId={l.to} direction="out" elementMap={elementMap} familyMap={familyMap} onSelect={onSelectElement} />)}
        </>
      )}
      {adj.in.length > 0 && (
        <>
          <div style={{ fontFamily: MONO, fontSize: 12, color: C.textMuted, margin: '12px 0 8px' }}>INFLUENCÉ PAR ({adj.in.length})</div>
          {adj.in.map(l => <LinkRow key={l.index} link={l} otherId={l.from} direction="in" elementMap={elementMap} familyMap={familyMap} onSelect={onSelectElement} />)}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────

const CSS = `
.tse-root *, .tse-root *::before, .tse-root *::after { box-sizing: border-box; }
.tse-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 340px); gap: 20px; align-items: start; }
@media (max-width: 900px) {
  .tse-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
.tse-node { cursor: pointer; transition: opacity .22s ease; outline: none; }
.tse-node:focus-visible .tse-ring { stroke: #fff; stroke-width: 3.5; }
.tse-band { transition: opacity .22s ease; }
.tse-band:hover path:first-child { stroke-width: 1.8; }
.tse-link { fill: none; transition: opacity .25s ease, stroke-width .25s ease; pointer-events: none; }
.tse-flow { stroke-dasharray: 7 7; animation: tse-dash 1.2s linear infinite; }
@keyframes tse-dash { to { stroke-dashoffset: -14; } }
.tse-chip, .tse-row { transition: border-color .15s ease, background .15s ease, transform .15s ease; font-family: inherit; }
.tse-chip:hover, .tse-row:hover { border-color: rgba(167,139,250,.55) !important; background: rgba(167,139,250,.08) !important; }
.tse-chip:focus-visible, .tse-row:focus-visible, .tse-root button:focus-visible { outline: 2px solid #a78bfa; outline-offset: 2px; }

@keyframes tse-pulse-violet {
  0% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(167, 139, 250, 0); }
  100% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0); }
}
.tse-unlock-pulse {
  animation: tse-pulse-violet 2s infinite;
}

@keyframes tse-blink-atlas {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
.tse-atlas-pulse {
  animation: tse-blink-atlas 2.5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .tse-flow, .tse-unlock-pulse, .tse-atlas-pulse { animation: none; }
  .tse-node, .tse-band, .tse-link, .tse-chip, .tse-row { transition: none; }
}
`;

export default TableauSystemique;